import type { FeatureFlagVariant } from '@/lib/feature-flags/types';

export const AUTH_EVENTS = {
  AUTH_SCREEN_VIEWED: 'auth_screen_viewed',
  SIGN_UP_CHOICE_CLICKED: 'sign_up_choice_clicked',
  SIGN_UP_EMAIL_RETURN_CLICKED: 'sign_up_email_return_clicked',
  SIGN_UP_STARTED: 'sign_up_started',
  SIGN_UP_EMAIL_CODE_SENT: 'sign_up_email_code_sent',
  SIGN_UP_EMAIL_VERIFIED: 'sign_up_email_verified',
  SIGN_UP_EMAIL_CODE_RESENT: 'sign_up_email_code_resent',
  SIGN_UP_EXISTING_ACCOUNT_HANDOFF: 'sign_up_existing_account_handoff',
  SIGN_UP_PASSWORD_OPTION_TOGGLED: 'sign_up_password_option_toggled',
  SIGN_UP_WEBSITE_CALLBACK_ARRIVED: 'sign_up_website_callback_arrived',
  SIGN_UP_PROVISION_STARTED: 'sign_up_provision_started',
  SIGN_UP_PROVISION_SUCCEEDED: 'sign_up_provision_succeeded',
  SIGN_UP_PROVISION_FAILED: 'sign_up_provision_failed',
  SIGN_UP_PROVISION_STUCK: 'sign_up_provision_stuck',
  SIGN_UP_COMPLETION_STAGE_TIMING: 'sign_up_completion_stage_timing',
  SIGN_UP_REDIRECT_STARTED: 'sign_up_redirect_started',
  SIGN_UP_APPLICATION_FIRST_CONTENT_VISIBLE: 'sign_up_application_first_content_visible',
  SIGN_IN_STARTED: 'sign_in_started',
  SIGN_IN_EMAIL_RETURN_CLICKED: 'sign_in_email_return_clicked',
  SIGN_IN_PASSWORD_SUBMITTED: 'sign_in_password_submitted',
  SIGN_IN_MFA_STARTED: 'sign_in_mfa_started',
  SIGN_IN_MFA_COMPLETED: 'sign_in_mfa_completed',
  SIGN_IN_MFA_BACKUP_CODE_SELECTED: 'sign_in_mfa_backup_code_selected',
  SIGN_IN_COMPLETED: 'sign_in_completed',
  SSO_SIGN_UP_COMPLETED: 'sign_up_sso_completed',
  SSO_SIGN_IN_COMPLETED: 'sign_in_sso_completed',
  SSO_TRANSFER_USED: 'auth_sso_transfer_used',
  SSO_EXISTING_SESSION_REUSED: 'auth_sso_existing_session_reused',
  SSO_FALLBACK_TO_SIGN_IN: 'auth_sso_fallback_to_sign_in',
  SSO_FALLBACK_TO_SIGN_UP: 'auth_sso_fallback_to_sign_up',
  SSO_SIGN_UP_RECOVERY_STARTED: 'auth_sso_sign_up_recovery_started',
  AUTH_UNAUTHENTICATED_REDIRECTED: 'auth_unauthenticated_redirected',
  AUTH_ERROR: 'auth_error',
  SIGN_IN_STRATEGY_SELECTED: 'sign_in_strategy_selected',
  SIGN_IN_SESSION_SELECTED: 'sign_in_session_selected',
  SIGN_UP_SESSION_SELECTED: 'sign_up_session_selected',
  SIGN_IN_FORGOT_PASSWORD_OPENED: 'sign_in_forgot_password_opened',
  SIGN_IN_RESET_CODE_REQUESTED: 'sign_in_reset_code_requested',
  SIGN_IN_RESET_CODE_SUBMITTED: 'sign_in_reset_code_submitted',
  SIGN_IN_RESET_CODE_RESENT: 'sign_in_reset_code_resent',
  SIGN_IN_NEW_PASSWORD_SUBMITTED: 'sign_in_new_password_submitted',
  SIGN_IN_CODE_RESENT: 'sign_in_code_resent',
} as const;

export type AuthEventName = (typeof AUTH_EVENTS)[keyof typeof AUTH_EVENTS];

export type AccountCreationMethod = 'email' | 'google';

export type AuthFlow = 'sign_in' | 'sign_up';
export type AuthFunnelStep =
  | 'start'
  | 'email_verification'
  | 'verifications'
  | 'choose_strategy'
  | 'forgot_password'
  | 'reset_password'
  | 'choose_session'
  | 'sso_callback'
  | 'website_callback'
  | 'sync';
export type AuthMethod = 'email' | 'google';
export type AuthStrategy = 'password' | 'email_code' | 'totp' | 'backup_code';
export type AuthEventResult = 'success' | 'error';

export interface AuthEventBaseProperties {
  ab_test_passwordless_sign_up_2026_q3?: FeatureFlagVariant;
  account_creation_method?: AccountCreationMethod;
  anonymous_id?: string;
  application_id?: number;
  current_screen?: string;
  error_code?: string;
  first_touch_portfolio_code?: string;
  flow?: AuthFlow;
  last_touch_portfolio_code?: string;
  method?: AuthMethod;
  newsletter?: boolean;
  partner?: string;
  portfolio_landed_at?: string;
  portfolio_referrer?: string | null;
  redirect_url?: string;
  redirected_screen?: string;
  result?: AuthEventResult;
  search_params?: Record<string, string>;
  step?: AuthFunnelStep;
  strategy?: AuthStrategy;
  utm_campaign?: string;
  utm_content?: string | null;
  utm_medium?: string;
  utm_referrer?: string;
  utm_source?: string;
}

export type SignUpChoiceClickedProperties = AuthEventBaseProperties & {
  choice: 'email' | 'google';
};
